import React, { useMemo, useCallback, useContext } from 'react';
import { Slider } from 'antd';
import type { SliderSingleProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

export interface AndSliderProps extends Omit<SliderSingleProps, 'onChange'>, BaseComponentProps, DataSetConfig {
  changeAction?: string;
  onChange?: (value: number) => void;
}

export const AndSlider: React.FC<AndSliderProps> = ({
  id, sessionId, develop, dataSet, dataSetField, changeAction, onChange, value: propValue, defaultValue, ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  const displayValue = useMemo(() => storeValue ?? propValue ?? defaultValue, [storeValue, propValue, defaultValue]);

  const handleChange = useCallback((value: number) => {
    if (onChange) { onChange(value); return; }
    if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, value);
    if (changeAction && sessionId) {
      const { actionName, arg } = parseChangeAction(changeAction, value, { id, dataSet, dataSetField }, ctx);
      addCommand(sessionId, createCommand(actionName, arg));
    }
  }, [onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, id, addCommand, ctx]);

  return <Slider {...restProps} disabled={develop ? false : restProps.disabled} value={displayValue} onChange={handleChange} />;
};

export default AndSlider;
